import {
  Field as BaseField,
  type FieldControlProps as BaseFieldControlProps,
  type FieldDescriptionProps as BaseFieldDescriptionProps,
  type FieldErrorProps as BaseFieldErrorProps,
  type FieldItemProps as BaseFieldItemProps,
  type FieldLabelProps as BaseFieldLabelProps,
  type FieldRootProps as BaseFieldRootProps,
} from "@base-ui/react/field";
import classNames from "classnames";
import * as React from "react";

import styles from "./Field.module.css";

/**
 * Narrows Base UI's `className`, which it types as a string *or* a function of
 * the part's state, to the string form: a state-dependent class puts a styling
 * decision at the call site that belongs in the module. `ref` is dropped
 * because `forwardRef` re-adds it, and a caller passing the other one would
 * have it silently ignored.
 */
type PartProps<P> = Omit<P, "className" | "ref"> & { className?: string };

export type FieldRootProps = PartProps<BaseFieldRootProps>;
export type FieldLabelProps = PartProps<BaseFieldLabelProps>;
export type FieldDescriptionProps = PartProps<BaseFieldDescriptionProps>;
export type FieldControlProps = PartProps<BaseFieldControlProps>;
export type FieldErrorProps = PartProps<BaseFieldErrorProps>;
export type FieldItemProps = PartProps<BaseFieldItemProps>;

/**
 * The field wrapper: names the control, describes it, and carries what is
 * wrong with it. Base UI's own controls (Select, Checkbox, Radio, Combobox)
 * read this context, so inside a `Field.Root` they pick up the name, the
 * invalid state, and the `aria-describedby` wiring without a `Field.Control`.
 *
 * Drive it from a react-hook-form `Controller`: `invalid`, `touched`, and
 * `dirty` come off `fieldState`.
 */
const Root = React.forwardRef<HTMLDivElement, FieldRootProps>(
  function FieldRoot({ className, ...props }, ref) {
    return (
      <BaseField.Root
        ref={ref}
        className={classNames(styles.root, className)}
        {...props}
      />
    );
  },
);

/** A `<label>` bound to the field's control. */
const Label = React.forwardRef<HTMLLabelElement, FieldLabelProps>(
  function FieldLabel({ className, ...props }, ref) {
    return (
      <BaseField.Label
        ref={ref}
        className={classNames(styles.label, className)}
        {...props}
      />
    );
  },
);

/** Helper text, wired into the control's `aria-describedby`. */
const Description = React.forwardRef<
  HTMLParagraphElement,
  FieldDescriptionProps
>(function FieldDescription({ className, ...props }, ref) {
  return (
    <BaseField.Description
      ref={ref}
      className={classNames(styles.description, className)}
      {...props}
    />
  );
});

/**
 * The native control. Pass `render={<TextInput />}` to swap ours in, so the
 * field's wiring lands on the input the app actually styles.
 */
const Control = React.forwardRef<HTMLInputElement, FieldControlProps>(
  function FieldControl({ className, ...props }, ref) {
    return (
      <BaseField.Control
        ref={ref}
        className={classNames(styles.control, className)}
        {...props}
      />
    );
  },
);

/**
 * The field error. `match` decides when it shows: `true` to always show it —
 * which is what an external validator such as zod wants — or a
 * [ValidityState](https://developer.mozilla.org/en-US/docs/Web/API/ValidityState)
 * key such as `"valueMissing"` to defer to the browser's own validation.
 */
const ErrorMessage = React.forwardRef<HTMLDivElement, FieldErrorProps>(
  function FieldError({ className, ...props }, ref) {
    return (
      <BaseField.Error
        ref={ref}
        className={classNames(styles.error, className)}
        {...props}
      />
    );
  },
);

/**
 * One option inside a group, so each radio or checkbox can carry its own
 * `Field.Label` and `Field.Description`.
 */
const Item = React.forwardRef<HTMLDivElement, FieldItemProps>(
  function FieldItem({ className, ...props }, ref) {
    return (
      <BaseField.Item
        ref={ref}
        className={classNames(styles.item, className)}
        {...props}
      />
    );
  },
);

/**
 * A form field: label, description, control, and error, wired together for
 * assistive technology.
 *
 * Reach for it around a control with no built-in `label` prop — `TextInput`,
 * `Textarea`, a condition builder. `Select`, `Checkbox`, and `RadioGroup`
 * already wrap themselves in one when given a `label` or `legend`.
 *
 * `Field` is itself `Field.Root`, the way `Combobox` is its own root, so the
 * parts hang off the same name the wrapper is written with.
 *
 * @example
 * ```tsx
 * <Field.Root name="name" invalid={fieldState.invalid}>
 *   <Field.Label>Name</Field.Label>
 *   <Field.Control render={<TextInput />} value={value} onChange={onChange} />
 *   <Field.Description>How you will find this trigger later.</Field.Description>
 *   <Field.Error match>{fieldState.error?.message}</Field.Error>
 * </Field.Root>
 * ```
 */
export const Field = Object.assign(Root, {
  Root,
  Label,
  Description,
  Control,
  Error: ErrorMessage,
  Item,
});
